/* El renderizador de Markdown del asistente — UNO, esta vez.
 *
 * El frontend anterior tenía dos: uno en `app/ui/chat/index.html` y una copia portada A MANO al
 * panel. Ya habían divergido. Este es el único, vive aquí, y se prueba.
 *
 * Es un subconjunto DELIBERADO: solo lo que el modelo emite de verdad, según la plantilla del
 * prompt (`app/llm.py`, «FORMATO DE LA RESPUESTA»). Nada más. Un parser completo de Markdown en una
 * superficie que muestra texto generado es superficie de ataque y de sorpresas: aquí no hay HTML en
 * bruto, ni imágenes, ni `javascript:`.
 *
 * Dos extensiones que NO son Markdown y que ya existen en el protocolo del servidor:
 *
 *   ```cifras              → tarjetas de cifra. «etiqueta | valor», el valor YA formateado por
 *   Gasto | $24,6 billones   el modelo, que tiene la regla en su prompt: «millones» y «billones»
 *   ```                      ESCRITOS COMPLETOS. Nada de «mM», «mil M», «B» ni «M» sueltas.
 *
 *   [Nombre](secop:entidad:800103920)   → enlace de exploración. `TIPO ∈ entidad|proveedor|persona
 *                                          |contrato`. Al tocarlo se indaga ese sujeto.
 *
 * Lo que se descarta a propósito: HTML embebido, imágenes, citas, encabezados que no sean `##`,
 * listas numeradas (el modelo numera con `## `) y énfasis con `_`. Si aparecen, se muestran como
 * texto plano — que es lo correcto: mejor un asterisco a la vista que una etiqueta interpretada.
 */

export type Inline =
  | { tipo: 'texto'; texto: string }
  | { tipo: 'fuerte'; texto: string }
  | { tipo: 'codigo'; texto: string }
  | { tipo: 'enlace'; texto: string; href: string }
  /** `[Nombre](secop:proveedor:900123456)` — no navega fuera: abre ese sujeto en el asistente. */
  | { tipo: 'secop'; texto: string; clase: ClaseSecop; id: string }

export type ClaseSecop = 'entidad' | 'proveedor' | 'persona' | 'contrato'
const CLASES: ClaseSecop[] = ['entidad', 'proveedor', 'persona', 'contrato']

export type Bloque =
  | { tipo: 'titulo'; texto: string }
  | { tipo: 'parrafo'; inline: Inline[] }
  | { tipo: 'lista'; items: Inline[][] }
  | { tipo: 'tabla'; cabeceras: Inline[][]; filas: Inline[][][] }
  | { tipo: 'cifras'; items: { etiqueta: string; valor: string }[] }
  | { tipo: 'codigo'; texto: string }

export function analizar(md: string): Bloque[] {
  const lineas = (md ?? '').replace(/\r\n?/g, '\n').split('\n')
  const bloques: Bloque[] = []
  let i = 0

  const parrafo: string[] = []
  const cerrarParrafo = () => {
    if (parrafo.length) {
      bloques.push({ tipo: 'parrafo', inline: enLinea(parrafo.join(' ')) })
      parrafo.length = 0
    }
  }

  while (i < lineas.length) {
    const linea = lineas[i] as string

    // ── bloque de código (y su extensión `cifras`) ──────────────────────────
    const valla = /^```(\w*)\s*$/.exec(linea.trim())
    if (valla) {
      cerrarParrafo()
      const lenguaje = (valla[1] ?? '').toLowerCase()
      const cuerpo: string[] = []
      i++
      // Sin cierre, se toma hasta el final: es una respuesta EN VIVO y el bloque puede estar a
      // medio escribir. Cortarla dejaría al lector mirando una respuesta truncada sin saberlo.
      while (i < lineas.length && !/^```/.test((lineas[i] as string).trim())) {
        cuerpo.push(lineas[i] as string)
        i++
      }
      i++
      if (lenguaje === 'cifras') {
        const items = cuerpo
          .map((l) => l.split('|'))
          .filter((p) => p.length >= 2)
          .map((p) => ({
            etiqueta: (p[0] as string).trim(),
            valor: p.slice(1).join('|').trim(),
          }))
          .filter((x) => x.etiqueta && x.valor)
        if (items.length) bloques.push({ tipo: 'cifras', items })
      } else if (cuerpo.length) {
        bloques.push({ tipo: 'codigo', texto: cuerpo.join('\n') })
      }
      continue
    }

    // ── título de sección ───────────────────────────────────────────────────
    const titulo = /^#{2,6}\s+(.+?)\s*$/.exec(linea)
    if (titulo) {
      cerrarParrafo()
      bloques.push({ tipo: 'titulo', texto: (titulo[1] as string).trim() })
      i++
      continue
    }

    // ── tabla ───────────────────────────────────────────────────────────────
    // Se exige la fila separadora: sin ella, una viñeta con dos «|» sueltos se convertiría en
    // tabla y la respuesta quedaría irreconocible.
    if (esFilaTabla(linea) && i + 1 < lineas.length && esSeparadorTabla(lineas[i + 1] as string)) {
      cerrarParrafo()
      const cabeceras = celdas(linea).map(enLinea)
      i += 2
      const filas: Inline[][][] = []
      while (i < lineas.length && esFilaTabla(lineas[i] as string)) {
        filas.push(celdas(lineas[i] as string).map(enLinea))
        i++
      }
      bloques.push({ tipo: 'tabla', cabeceras, filas })
      continue
    }

    // ── lista ───────────────────────────────────────────────────────────────
    if (/^\s*[-•*]\s+/.test(linea)) {
      cerrarParrafo()
      const items: Inline[][] = []
      while (i < lineas.length && /^\s*[-•*]\s+/.test(lineas[i] as string)) {
        items.push(enLinea((lineas[i] as string).replace(/^\s*[-•*]\s+/, '')))
        i++
      }
      bloques.push({ tipo: 'lista', items })
      continue
    }

    if (!linea.trim()) {
      cerrarParrafo()
      i++
      continue
    }

    parrafo.push(linea.trim())
    i++
  }
  cerrarParrafo()
  return bloques
}

const esFilaTabla = (l: string) => /^\s*\|.*\|\s*$/.test(l)
const esSeparadorTabla = (l: string) => /^\s*\|[\s:|-]+\|\s*$/.test(l) && l.includes('-')

function celdas(linea: string): string[] {
  return linea.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim())
}

/**
 * Trozos en línea: enlaces, negrita y código. En ESE orden.
 *
 * El orden importa: el texto de un enlace puede traer negrita, pero un `**` no puede envolver un
 * enlace a medias. Resolviendo primero los enlaces, lo de dentro se procesa como texto normal y no
 * hay corchetes sueltos que luego rompan la negrita.
 */
export function enLinea(texto: string): Inline[] {
  const fuera: Inline[] = []
  const patron = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`/g
  let ultimo = 0
  let m: RegExpExecArray | null
  while ((m = patron.exec(texto)) !== null) {
    if (m.index > ultimo) fuera.push({ tipo: 'texto', texto: texto.slice(ultimo, m.index) })
    if (m[1] !== undefined && m[2] !== undefined) {
      fuera.push(enlace(m[1], m[2]))
    } else if (m[3] !== undefined) {
      fuera.push({ tipo: 'fuerte', texto: m[3] })
    } else if (m[4] !== undefined) {
      fuera.push({ tipo: 'codigo', texto: m[4] })
    }
    ultimo = patron.lastIndex
  }
  if (ultimo < texto.length) fuera.push({ tipo: 'texto', texto: texto.slice(ultimo) })
  return fuera.length ? fuera : [{ tipo: 'texto', texto }]
}

/**
 * Un destino solo puede ser `secop:` o `http(s)`. Cualquier otro esquema —`javascript:`, `data:`,
 * `vbscript:`— se degrada a TEXTO, no a enlace.
 *
 * No es paranoia teórica: el texto lo escribe un modelo de lenguaje sobre datos que vienen de
 * SECOP, y basta con que un objeto de contrato traiga algo con pinta de enlace para que el modelo
 * lo copie. Es la misma regla de `urlSegura()` en `formato.ts`, aplicada donde se decide.
 */
function enlace(texto: string, href: string): Inline {
  const m = /^secop:(entidad|proveedor|persona|contrato):([\w.-]+)$/i.exec(href)
  if (m) {
    const clase = (m[1] as string).toLowerCase() as ClaseSecop
    if (CLASES.includes(clase)) return { tipo: 'secop', texto, clase, id: m[2] as string }
  }
  if (/^https?:\/\//i.test(href)) return { tipo: 'enlace', texto, href }
  return { tipo: 'texto', texto }
}

/**
 * Parte la respuesta en «respuesta corta» y el resto.
 *
 * La plantilla del prompt pide empezar con UNA frase de síntesis que responda directo. El rediseño
 * la saca del muro y la pone arriba, en serif grande, con su rótulo: es lo único que mucha gente va
 * a leer. Si la respuesta no empieza por un párrafo —arranca con un título, una tabla o las
 * cifras— NO se inventa una: se devuelve `null` y la respuesta se pinta entera y de corrido.
 */
export function partirRespuesta(bloques: Bloque[]): { corta: Inline[] | null; resto: Bloque[] } {
  const primero = bloques[0]
  if (primero?.tipo !== 'parrafo') return { corta: null, resto: bloques }
  return { corta: primero.inline, resto: bloques.slice(1) }
}

/** El texto plano de una respuesta, para el botón «Copiar». */
export function aTextoPlano(inline: Inline[]): string {
  return inline.map((t) => t.texto).join('')
}

/* ── el informe del pliego ────────────────────────────────────────────────────
   Su sección «Requisitos que podrían dejarte fuera» no es una lista normal: cada viñeta empieza con
   la severidad literal `[Alta]`/`[Media]`/`[Baja]` y trae la cita VERBATIM entre comillas angulares.
   Lo pide así el prompt de `app/analizar_proceso.py`, y el rediseño lo pinta como tarjetas con
   filete de color. Partirlo aquí —y no dentro del componente— es lo que permite probarlo. */

export type Severidad = 'alta' | 'media' | 'baja'

export interface Hallazgo {
  severidad: Severidad | null
  /** Lo que dice el hallazgo, sin la etiqueta de severidad y sin la cita. */
  texto: Inline[]
  /** La cita textual del pliego, sin las comillas angulares. */
  cita: string | null
}

const SEVERIDADES: Record<string, Severidad> = { alta: 'alta', media: 'media', baja: 'baja' }

/**
 * Parte una viñeta de hallazgo en severidad, prosa y cita.
 *
 * Lo que NO hace, a propósito: inventar la severidad cuando el modelo no la pone. Sin `[Alta]` el
 * hallazgo sale con `severidad: null` y se pinta en neutro. Suponer «media» sería atribuirle al
 * pliego una gravedad que nadie midió — y aquí el color no informa solo.
 */
/**
 * ¿De qué requisito habilita habla esta viñeta?
 *
 * Las claves son EXACTAMENTE las de `COMPARABLES` en `app/veredicto.py`. Añadir una allí sin
 * añadirla aquí no rompe nada visible: el hallazgo simplemente no se vincula y se pinta como antes.
 * Ese es el modo de fallo que se quería —degradar, no mentir—, pero conviene tocar los dos a la vez.
 */
const SENALES: [string, RegExp][] = [
  ['liquidez_min', /liquidez/i],
  ['endeudamiento_max', /endeudamiento/i],
  ['cobertura_min', /cobertura\s+de\s+intereses/i],
  ['roa_min', /rentabilidad\s+(?:sobre|de)\s+(?:los\s+)?activos?|\bROA\b/i],
  ['roe_min', /rentabilidad\s+(?:sobre|del?)\s+(?:el\s+)?patrimonio|\bROE\b/i],
  ['capital_trabajo_min', /capital\s+de\s+trabajo/i],
  ['patrimonio_min', /patrimonio\s+m[íi]nimo/i],
  ['exp_n_contratos', /n[úu]mero\s+de\s+contratos|cantidad\s+de\s+contratos/i],
  ['exp_valor_smmlv', /\bSMMLV\b|salarios\s+m[íi]nimos/i],
  ['rup_exigido', /\bRUP\b|registro\s+[úu]nico\s+de\s+proponentes/i],
]

/** Qué dice tu perfil sobre un hallazgo, si es que dice algo. */
export interface Cruce {
  /** `true` = lo cumples todo · `false` = algo te deja fuera · `'parcial'` = falta verificar algo */
  cumples: boolean | 'parcial'
  /** Los requisitos vinculados, ya comparados POR EL SERVIDOR. */
  requisitos: { clave: string; etiqueta: string; exigido: string; tuyo: string; signo: string }[]
}

/**
 * Cruza un hallazgo de «Requisitos que podrían dejarte fuera» con lo que TU perfil acredita.
 *
 * Existe por una contradicción que el informe tenía consigo mismo: la sección de hallazgos
 * marcaba en ocre un requisito de liquidez «elevado», y cuatro párrafos más abajo la sección del
 * cruce decía que la tuya lo supera sesenta veces. La misma exigencia pintada como amenaza y como
 * cumplida. Son dos preguntas distintas —«¿esto restringe la competencia?» y «¿esto te deja fuera
 * a TI?»— y el informe solo respondía la primera con el color.
 *
 * ⚠ Aquí NO se compara nada ni se decide si alguien cumple: eso lo hizo `app/veredicto.py`, que es
 * una función pura y probada, y llega ya resuelto en `signo`. Lo único que ocurre aquí es
 * reconocer de qué requisito habla la viñeta. Si no se reconoce ninguno, se devuelve `null` y el
 * hallazgo se pinta exactamente como antes.
 *
 * Un `sin_dato` no cuenta como incumplimiento —«no pudimos verificarlo» no es «no cumples»— pero
 * impide afirmar que se cumple del todo: de ahí el estado `'parcial'`.
 */
export function cruzarHallazgo(
  h: Hallazgo,
  requisitos: { clave: string; etiqueta: string; exigido: string; tuyo: string; signo: string }[],
): Cruce | null {
  if (!requisitos.length) return null
  const texto = h.texto.map((t) => t.texto).join('') + ' ' + (h.cita ?? '')
  const claves = new Set(SENALES.filter(([, re]) => re.test(texto)).map(([k]) => k))
  if (!claves.size) return null

  const tocados = requisitos.filter((r) => claves.has(r.clave))
  if (!tocados.length) return null

  const cumples = tocados.some((r) => r.signo === 'no')
    ? false
    : tocados.some((r) => r.signo === 'sin_dato')
      ? (tocados.some((r) => r.signo === 'ok') ? 'parcial' : null)
      : true
  // Solo «sin_dato»: no se sabe nada, así que no se dice nada. Una insignia de «no verificado»
  // sobre un hallazgo que ya avisa de un riesgo solo añadiría ruido.
  if (cumples === null) return null
  return { cumples, requisitos: tocados }
}

export function partirHallazgo(inline: Inline[]): Hallazgo {
  const plano = inline.map((t) => t.texto).join('')
  const m = /^\s*\[\s*(alta|media|baja)\s*\]\s*/i.exec(plano)
  const severidad = m ? (SEVERIDADES[(m[1] as string).toLowerCase()] as Severidad) : null

  // La cita va entre comillas angulares. Se toma la MÁS LARGA: el modelo usa a veces las angulares
  // también para un inciso corto, y la que importa es la del pliego.
  let cita: string | null = null
  for (const c of plano.matchAll(/«([^»]{15,})»/g)) {
    const t = (c[1] as string).trim()
    if (!cita || t.length > cita.length) cita = t
  }

  const limpio = plano
    .replace(/^\s*\[\s*(alta|media|baja)\s*\]\s*/i, '')
    .replace(cita ? `«${cita}»` : '', '')
    // Sacar la cita del medio deja la puntuación que la envolvía: «…del contrato: . Esto podría…».
    // Se cierra el hueco — dos signos seguidos se leen como un fallo de la página, no del pliego.
    .replace(/([:,;]|\s)\s*([.,;:])/g, '$2')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .trim()
  return { severidad, texto: enLinea(limpio), cita }
}
